import { useState, useEffect, useCallback } from 'react';
import { colors, radii, shadows, typography, fontWeights } from '../styles/designTokens';
import { API_BASE_URL } from '../config/environment';

// Extracted phase renderers to reduce component complexity

function ValidatingPhase() {
  return (
    <div style={{ textAlign: 'center', padding: '20px 0' }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: `3px solid ${colors.divider}`,
        borderTopColor: colors.info,
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
        margin: '0 auto 16px',
      }} />
      <p style={{
        fontSize: typography.body.fontSize,
        color: colors.textSecondary,
        margin: 0,
      }}>
        Validating your invite link...
      </p>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

function CreatingPhase() {
  return (
    <div style={{ textAlign: 'center', padding: '20px 0' }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: `3px solid ${colors.divider}`,
        borderTopColor: colors.success,
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
        margin: '0 auto 16px',
      }} />
      <p style={{
        fontSize: typography.body.fontSize,
        color: colors.textSecondary,
        margin: 0,
      }}>
        Creating your account...
      </p>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

function SuccessPhase() {
  return (
    <div style={{ textAlign: 'center', padding: '20px 0' }}>
      <div style={{
        fontSize: '48px',
        marginBottom: '16px',
      }}>
        🎉
      </div>
      <p style={{
        fontSize: typography.subheading.fontSize,
        fontWeight: fontWeights.medium,
        color: colors.success,
        margin: '0 0 8px 0',
      }}>
        Account created successfully!
      </p>
      <p style={{
        fontSize: typography.body.fontSize,
        color: colors.textSecondary,
        margin: 0,
      }}>
        Redirecting you to TravelFlow...
      </p>
    </div>
  );
}

function ErrorPhase({ errorMessage, errorType, onRetry }) {
  return (
    <div style={{ textAlign: 'center', padding: '20px 0' }}>
      <div style={{
        padding: '16px',
        borderRadius: radii.inner,
        marginBottom: '20px',
        backgroundColor: colors.urgentBackground,
        border: '1px solid #fecaca',
      }}>
        <p style={{
          fontSize: typography.body.fontSize,
          color: colors.urgent,
          margin: 0,
          lineHeight: '1.5',
        }}>
          {errorMessage}
        </p>
      </div>

      {/* Only show retry for network errors */}
      {errorType === 'network' && (
        <button
          onClick={onRetry}
          style={{
            padding: '12px 24px',
            backgroundColor: '#2563eb',
            color: 'white',
            border: 'none',
            borderRadius: radii.inner,
            fontSize: typography.body.fontSize,
            fontWeight: fontWeights.semibold,
            cursor: 'pointer',
            transition: 'background-color 0.15s',
          }}
        >
          Try Again
        </button>
      )}
    </div>
  );
}

/**
 * InviteAcceptPage — Magic link redemption page.
 *
 * State machine: validating → form → creating → success | error
 *
 * On mount, validates the invite token against the backend.
 * On success, renders an account creation form with email pre-filled.
 * On password submit, creates the account and stores session tokens.
 * On error, displays a contextual message without navigating away.
 */
const InviteAcceptPage = ({ token }) => {
  const [phase, setPhase] = useState('validating');
  const [email, setEmail] = useState(null);
  const [errorType, setErrorType] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(null);

  // Map backend error messages to error types
  const classifyError = useCallback((message) => {
    if (!message) return { type: 'network', message: 'An unexpected error occurred. Please try again.' };

    if (message.includes('Invalid invite link')) {
      return { type: 'invalid', message: 'This invite link is invalid. Please request a new invitation.' };
    }
    if (message.includes('expired')) {
      return { type: 'expired', message: 'This invite link has expired. Please request a new invitation.' };
    }
    if (message.includes('already been used')) {
      return { type: 'used', message: 'This invite link has already been used. If you already have an account, please log in.' };
    }
    return { type: 'network', message: message || 'Something went wrong. Please try again.' };
  }, []);

  // Phase 1: Validate token on mount
  useEffect(() => {
    if (!token) {
      setPhase('error');
      setErrorType('invalid');
      setErrorMessage('No invite token provided. Please check your invite link.');
      return;
    }

    let cancelled = false;

    const validateToken = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/invites/accept`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });

        if (cancelled) return;

        const data = await response.json();

        if (response.ok && data.valid) {
          setEmail(data.email);
          setPhase('form');
        } else {
          const classified = classifyError(data.error);
          setErrorType(classified.type);
          setErrorMessage(classified.message);
          setPhase('error');
        }
      } catch (err) {
        if (cancelled) return;
        setErrorType('network');
        setErrorMessage('Unable to connect. Please check your internet connection and try again.');
        setPhase('error');
      }
    };

    validateToken();
    return () => { cancelled = true; };
  }, [token, classifyError]);

  // Phase 2: Submit password for account creation
  const handleSubmit = async (e) => {
    e.preventDefault();
    setPasswordError(null);

    // Basic password validation
    if (!password || password.length < 8) {
      setPasswordError('Password must be at least 8 characters.');
      return;
    }

    setPhase('creating');

    try {
      const response = await fetch(`${API_BASE_URL}/invites/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Store session tokens
        localStorage.setItem('idToken', data.idToken);
        localStorage.setItem('accessToken', data.accessToken);
        localStorage.setItem('refreshToken', data.refreshToken);

        setPhase('success');

        // Redirect to main app after brief delay
        setTimeout(() => {
          window.location.href = '/';
        }, 1500);
      } else {
        const classified = classifyError(data.error);
        setErrorType(classified.type);
        setErrorMessage(classified.message);
        // On Cognito failure, go back to form so user can retry
        setPhase('form');
      }
    } catch (err) {
      setErrorType('network');
      setErrorMessage('Unable to connect. Please check your internet connection and try again.');
      // Stay on form for retry
      setPhase('form');
    }
  };

  // Retry validation from error state
  const handleRetryValidation = () => {
    setErrorType(null);
    setErrorMessage(null);
    setPhase('validating');

    // Re-trigger validation
    const validateToken = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/invites/accept`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });

        const data = await response.json();

        if (response.ok && data.valid) {
          setEmail(data.email);
          setPhase('form');
        } else {
          const classified = classifyError(data.error);
          setErrorType(classified.type);
          setErrorMessage(classified.message);
          setPhase('error');
        }
      } catch (err) {
        setErrorType('network');
        setErrorMessage('Unable to connect. Please check your internet connection and try again.');
        setPhase('error');
      }
    };

    validateToken();
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '24px',
    }}>
      <div style={{
        backgroundColor: colors.surface,
        borderRadius: radii.card,
        boxShadow: shadows.card,
        padding: '40px',
        maxWidth: '440px',
        width: '100%',
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <h1 style={{
            fontSize: '28px',
            fontWeight: fontWeights.bold,
            color: colors.textPrimary,
            margin: '0 0 8px 0',
          }}>
            ✈️ TravelFlow
          </h1>
          <p style={{
            fontSize: typography.body.fontSize,
            color: colors.textSecondary,
            margin: 0,
          }}>
            {phase === 'validating' && 'Verifying your invite...'}
            {phase === 'form' && 'Create your account'}
            {phase === 'creating' && 'Setting up your account...'}
            {phase === 'success' && 'Welcome aboard!'}
            {phase === 'error' && 'Something went wrong'}
          </p>
        </div>

        {/* Validating State */}
        {phase === 'validating' && <ValidatingPhase />}

        {/* Form State */}
        {phase === 'form' && (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: typography.body.fontSize,
                fontWeight: fontWeights.medium,
                color: colors.cardBodyText,
                marginBottom: '6px',
              }}>
                Email Address
              </label>
              <input
                type="email"
                value={email || ''}
                disabled
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: `1px solid ${colors.divider}`,
                  borderRadius: radii.inner,
                  fontSize: typography.subheading.fontSize,
                  backgroundColor: colors.surfaceAlt,
                  color: colors.textSecondary,
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: typography.body.fontSize,
                fontWeight: fontWeights.medium,
                color: colors.cardBodyText,
                marginBottom: '6px',
              }}>
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (passwordError) setPasswordError(null);
                }}
                placeholder="Minimum 8 characters"
                autoFocus
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: `1px solid ${passwordError ? colors.urgent : colors.divider}`,
                  borderRadius: radii.inner,
                  fontSize: typography.subheading.fontSize,
                  boxSizing: 'border-box',
                }}
              />
              {passwordError && (
                <p style={{
                  fontSize: typography.caption.fontSize,
                  color: colors.urgent,
                  margin: '6px 0 0 0',
                }}>
                  {passwordError}
                </p>
              )}
            </div>

            {/* Show inline error if account creation failed (retry scenario) */}
            {errorMessage && (
              <div style={{
                padding: '12px',
                borderRadius: radii.inner,
                marginBottom: '16px',
                backgroundColor: colors.urgentBackground,
                border: '1px solid #fecaca',
                color: colors.urgent,
                fontSize: typography.body.fontSize,
              }}>
                {errorMessage}
              </div>
            )}

            <button
              type="submit"
              style={{
                width: '100%',
                padding: '14px',
                backgroundColor: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: radii.inner,
                fontSize: typography.subheading.fontSize,
                fontWeight: fontWeights.semibold,
                cursor: 'pointer',
                transition: 'background-color 0.15s',
              }}
            >
              Create Account
            </button>
          </form>
        )}

        {/* Creating State */}
        {phase === 'creating' && <CreatingPhase />}

        {/* Success State */}
        {phase === 'success' && <SuccessPhase />}

        {/* Error State */}
        {phase === 'error' && (
          <ErrorPhase
            errorMessage={errorMessage}
            errorType={errorType}
            onRetry={handleRetryValidation}
          />
        )}
      </div>
    </div>
  );
};

export default InviteAcceptPage;
